// ════════════════════════════════════════════════════════════════════════════
//  features/fiscal/transporte/hooks/useCTe.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { cteService, cteOsService } from '../services/cteService';
import type {
  EmitirCTeRequest,
  EmitirCTeOsRequest,
  CancelarCTeRequest,
  CartaCorrecaoCTeRequest,
  ListaTransporteFiltros,
} from '../types';

// ── CT-e mod 57 ───────────────────────────────────────────────────────────────

const KEYS_CTE = {
  all:     ['cte'] as const,
  lista:   (p: number, t: number, filtros: ListaTransporteFiltros) => ['cte', 'lista', p, t, filtros] as const,
  detalhe: (id: number)           => ['cte', id] as const,
};

export const useCTe = (pagina = 1, tamanhoPagina = 20, filtros: ListaTransporteFiltros = {}) => {
  const qc = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: KEYS_CTE.lista(pagina, tamanhoPagina, filtros),
    queryFn:  () => cteService.listar(pagina, tamanhoPagina, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada letra da busca
  });

  const docs        = paginado?.itens        ?? [];
  const total       = paginado?.total         ?? 0;
  const totalPaginas = paginado?.totalPaginas ?? 1;
  const contagem    = paginado?.contagem;

  const emitir = useMutation({
    mutationFn: (req: EmitirCTeRequest) => cteService.emitir(req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS_CTE.all });
      if (res.sucesso) {
        toast.success(`CT-e emitido — Protocolo: ${res.protocolo ?? '-'}`);
      } else {
        toast.warning(`SEFAZ: ${res.cStat} — ${res.xMotivo}`);
      }
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const cancelar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: CancelarCTeRequest }) => cteService.cancelar(id, req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS_CTE.all });
      if (res.sucesso) {
        toast.success('CT-e cancelado com sucesso');
      } else {
        toast.warning(`SEFAZ: ${res.cStat} — ${res.xMotivo}`);
      }
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const cartaCorrecao = useMutation({
    mutationFn: ({ id, req }: { id: number; req: CartaCorrecaoCTeRequest }) => cteService.cartaCorrecao(id, req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS_CTE.all });
      if (res.sucesso) {
        toast.success('Carta de Correção enviada');
      } else {
        toast.warning(`SEFAZ: ${res.cStat} — ${res.xMotivo}`);
      }
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return {
    docs, total, totalPaginas, contagem, isLoading, isFetching,
    emitir,
    cancelar,
    cartaCorrecao,
  };
};

export const useCTeDetalhe = (id: number) =>
  useQuery({
    queryKey: KEYS_CTE.detalhe(id),
    queryFn:  () => cteService.obterPorId(id),
    enabled:  id > 0,
  });

// ── CT-e OS mod 67 ────────────────────────────────────────────────────────────

const KEYS_CTEOS = {
  all:   ['cte-os'] as const,
  lista: (p: number, t: number, filtros: ListaTransporteFiltros) => ['cte-os', 'lista', p, t, filtros] as const,
};

export const useCTeOS = (pagina = 1, tamanhoPagina = 20, filtros: ListaTransporteFiltros = {}) => {
  const qc = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: KEYS_CTEOS.lista(pagina, tamanhoPagina, filtros),
    queryFn:  () => cteOsService.listar(pagina, tamanhoPagina, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada letra da busca
  });

  const docs         = paginado?.itens        ?? [];
  const total        = paginado?.total         ?? 0;
  const totalPaginas = paginado?.totalPaginas  ?? 1;
  const contagem     = paginado?.contagem;

  const emitir = useMutation({
    mutationFn: (req: EmitirCTeOsRequest) => cteOsService.emitir(req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS_CTEOS.all });
      if (res.sucesso) {
        toast.success(`CT-e OS emitido — Protocolo: ${res.protocolo ?? '-'}`);
      } else {
        toast.warning(`SEFAZ: ${res.cStat} — ${res.xMotivo}`);
      }
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const cancelar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: CancelarCTeRequest }) => cteOsService.cancelar(id, req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS_CTEOS.all });
      if (res.sucesso) toast.success('CT-e OS cancelado com sucesso');
      else toast.warning(`SEFAZ: ${res.cStat} — ${res.xMotivo}`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const cartaCorrecao = useMutation({
    mutationFn: ({ id, req }: { id: number; req: CartaCorrecaoCTeRequest }) => cteOsService.cartaCorrecao(id, req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS_CTEOS.all });
      if (res.sucesso) toast.success('Carta de Correção enviada');
      else toast.warning(`SEFAZ: ${res.cStat} — ${res.xMotivo}`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return { docs, total, totalPaginas, contagem, isLoading, isFetching, emitir, cancelar, cartaCorrecao };
};
